import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import DemoModal from '../components/DemoModal';
import PhoneAnimation from '../components/PhoneAnimation';
import VideoPlayPauseButton from '../components/VideoPlayPauseButton';
import VolumeControl from '../components/VolumeControl';
import { getCloudflarePosterUrl } from '../utils/cloudflareStream';
import { ArrowRight, Phone, MessageSquare, Calendar, Brain, Zap, Clock } from 'lucide-react';

const SCALE_VIDEO_ID = '8d6b00c3e8ffc6d9e14dd8b86496a9b4';

const ScaleVideoFrame = ({ cloudflareVideoId }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().then(() => setIsPlaying(true)).catch(() => {});
        } else {
          video.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const togglePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  return (
    <div
      className="group/video relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
      style={{
        aspectRatio: '16 / 9',
        border: '2px solid #D4AF37',
        boxShadow:
          'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
      }}
      data-testid="ai-concierge-scale-video-placeholder"
    >
      <video
        ref={videoRef}
        src={`https://customer-q87c1i5e2o40bvjt.cloudflarestream.com/${cloudflareVideoId}/downloads/default.mp4`}
        poster={getCloudflarePosterUrl(cloudflareVideoId)}
        muted={isMuted}
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
        data-testid={`ai-concierge-scale-video-${cloudflareVideoId}`}
      />
      <VideoPlayPauseButton
        isPlaying={isPlaying}
        onClick={togglePlayPause}
        testId={`ai-concierge-scale-video-play-button-${cloudflareVideoId}`}
        motionDelay={0}
      />
      <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 md:bottom-6 md:right-6 z-20 flex items-center gap-2 sm:gap-3">
        <VolumeControl
          videoRef={videoRef}
          isMuted={isMuted}
          onToggleMute={toggleMute}
          testId={`ai-concierge-scale-video-volume-button-${cloudflareVideoId}`}
          motionDelay={0}
        />
      </div>
    </div>
  );
};

const ProductA = () => {
  const [demoOpen, setDemoOpen] = useState(false);

  const features = [
    { 
      icon: Phone,
      title: 'AI-Native Voice', 
      description: 'Natural conversations powered by advanced AI' 
    },
    { 
      icon: MessageSquare,
      title: 'Multi-Channel Support', 
      description: 'Phone, web, mobile — everywhere' 
    },
    { 
      icon: Brain,
      title: 'Real-time Intelligence', 
      description: 'Instant insights and responses' 
    },
    { 
      icon: Clock,
      title: '24/7 Availability', 
      description: 'Never miss a customer interaction' 
    },
    { 
      icon: Zap,
      title: 'Seamless Integration', 
      description: 'Works with your existing systems' 
    },
    { 
      icon: Calendar,
      title: 'Smart Escalation', 
      description: 'Knows when to hand off to humans' 
    },
  ];

  return (
    <div className="w-full">
      {/* Hero — three-column: AI Concierge text · Phone mockup · Live Translator text */}
      <section className="min-h-[85vh] bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7] flex items-center justify-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_auto_1fr] gap-12 xl:gap-10 items-center">
            {/* Left — AI Concierge text */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="xl:pr-4 xl:border-r xl:border-black/10 xl:text-right xl:justify-self-end"
            >
              <h1 className="text-4xl md:text-5xl font-medium text-black/95 mb-6">
                AI Concierge
              </h1>
              <p className="text-base md:text-lg text-black/70 max-w-md xl:ml-auto">
                Your AI-native Voice Agent that handles customer interactions with elegance and intelligence. Never miss a call again.
              </p>
            </motion.div>

            {/* Center — Phone mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center w-full xl:w-[19rem]"
            >
              <PhoneAnimation />
            </motion.div>

            {/* Right — Live Translator text */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="xl:pl-4 xl:border-l xl:border-black/10 xl:justify-self-start"
              data-testid="live-translator-text"
            >
              <h2 className="text-4xl md:text-5xl font-medium text-black/95 mb-6">
                Live Translator
              </h2>
              <p className="text-base md:text-lg text-black/70 max-w-md">
                Any country, any spoken language — understood instantly, in real time.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features - min-h-[85vh] */}
      <section className="min-h-[85vh] bg-white flex items-center justify-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
          <motion.div
            className="text-center mb-10 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4">
              Unlock Powerful AI-Driven Interactions
            </h2>
            <p className="text-lg text-black/70 max-w-2xl mx-auto">
              GoogLyn's AI voice and translation agents deliver human-like interactions with the reliability and efficiency your business needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className="h-full bg-[#E8E4DC] border-[#F4C2D4]/30 hover:bg-[#FDF7F9] hover:border-[#E8A0B8]/50 hover:-translate-y-1 transition-all duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-xl bg-[#E8A0B8]/10 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-[#E8A0B8]" />
                    </div>
                    <CardTitle className="text-black/90 text-lg font-sans">
                      {feature.title}
                    </CardTitle>
                    <CardDescription className="text-black/70 text-sm">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Content Section - min-h-[85vh] */}
      <section className="min-h-[85vh] bg-[#F5F7F5] flex items-center justify-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4">
                Built for Scale
              </h2>
              <p className="text-lg text-black/70 mb-8">
                Handle thousands of conversations and translations simultaneously with enterprise-grade reliability and performance.
              </p>
              <div className="flex flex-col gap-6">
                <Card className="bg-white border-[#D4DDD1]/40">
                  <CardHeader>
                    <CardTitle className="text-xl font-medium text-black/90 mb-2">
                      Enterprise Ready
                    </CardTitle>
                    <CardDescription className="text-black/70">
                      Built to SOC 2 standards, 99.9% uptime SLA, dedicated support teams, and custom integrations.
                    </CardDescription>
                  </CardHeader>
                </Card>
                <Card className="bg-white border-[#D4DDD1]/40">
                  <CardHeader>
                    <CardTitle className="text-xl font-medium text-black/90 mb-2">
                      Proven Results
                    </CardTitle>
                    <CardDescription className="text-black/70">
                      95% customer satisfaction, 60% cost reduction, 10x faster response times.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div
                className="relative w-full"
                data-testid="ai-concierge-scale-video-placeholder"
              >
                <ScaleVideoFrame cloudflareVideoId={SCALE_VIDEO_ID} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-[#E8EDE7] to-[#D4DDD1]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-medium text-black/95 mb-6">
              Ready to get started?
            </h2>
            <p className="text-lg text-black/70 mb-8 max-w-2xl mx-auto">
              Experience elegant intelligence. Book a demo today.
            </p>
            <Button
              size="lg"
              onClick={() => setDemoOpen(true)}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105"
            >
              Book a demo
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
};

export default ProductA;
